/**
 * 「模型与供应商」五页共用的小控件：Logo 方块、iOS 开关、行内操作菜单、摘要卡、状态点与格式化函数。
 *
 * 背景：Alice 的供应商列表、模型表、渠道模型库都用同一套 Logo 方块、开关与「更多」菜单，样式在 alice-admin.css
 *   （.provider-item-icon、.ios-toggle、.action-menu、.model-summary-card）。kit.tsx 里没有对应组件。
 * 设计意图：把 Alice 的 DOM 与类名原样收进组件，页面只写业务；不改 kit.tsx 与 kit-runtime2.tsx 的既有导出。
 * 关键约束：Logo 地址由运营填写，图片加载失败时退回首字母，不让破图撑开布局；菜单用 <details>，点菜单项后要手动收起。
 */
import { useRef, type CSSProperties, type ReactNode } from 'react'
import './catalog.css'

/** 取名称的前两个字符做 Logo 缺省字母。 */
export const initials = (name: string) => (name.trim().slice(0, 2) || '?').toUpperCase()

/** Logo 方块：有地址显示图片，没有或加载失败显示首字母。size 为 'sm'（28px 模型）或 'md'（32px 供应商）或 'lg'（44px 详情）。 */
export function Logo({ url, name, size = 'md' }: { url?: string; name: string; size?: 'sm' | 'md' | 'lg' }) {
  const cls = size === 'sm' ? 'model-icon-wrap' : size === 'lg' ? 'detail-icon' : 'provider-item-icon'
  return (
    <div className={cls} title={name} style={size === 'sm' ? { fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)' } : undefined}>
      {url ? (
        <img
          src={url}
          alt=""
          style={size === 'lg' ? { width: 28, height: 28, objectFit: 'contain' } : undefined}
          onError={(e) => {
            // 图片地址失效时退回首字母
            const el = e.currentTarget
            el.style.display = 'none'
            if (el.parentElement && !el.parentElement.dataset.fallback) {
              el.parentElement.dataset.fallback = '1'
              el.parentElement.append(initials(name))
            }
          }}
        />
      ) : (
        initials(name)
      )}
    </div>
  )
}

/** iOS 样式开关（.ios-toggle）。 */
export function IosToggle({ on, onChange, disabled, title }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean; title?: string }) {
  return (
    <div
      className={`ios-toggle${on ? ' on' : ''}`}
      role="switch"
      aria-checked={on}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      title={title}
      style={disabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
      onClick={() => !disabled && onChange(!on)}
      onKeyDown={(e) => {
        if (!disabled && (e.key === ' ' || e.key === 'Enter')) {
          e.preventDefault()
          onChange(!on)
        }
      }}
    >
      <div className="ios-track" />
      <div className="ios-thumb" />
    </div>
  )
}

/** 行内「更多」菜单（.action-menu）；点任一菜单项后自动收起。 */
export function ActionMenu({ label, children, dropUp }: { label: ReactNode; children: ReactNode; dropUp?: boolean }) {
  const ref = useRef<HTMLDetailsElement>(null)
  return (
    <details ref={ref} className={`action-menu${dropUp ? ' drop-up' : ''}`}>
      <summary>{label}</summary>
      <div className="action-menu-pop" onClick={() => ref.current?.removeAttribute('open')}>
        {children}
      </div>
    </details>
  )
}

/** 摘要卡网格（.model-summary-grid）。 */
export function SummaryGrid({ children }: { children: ReactNode }) {
  return <div className="model-summary-grid">{children}</div>
}

export function SummaryCard({ label, value, hint, valueStyle }: { label: ReactNode; value: ReactNode; hint?: ReactNode; valueStyle?: CSSProperties }) {
  return (
    <div className="model-summary-card">
      <div className="label">{label}</div>
      <div className="value" style={valueStyle}>
        {value}
      </div>
      {hint != null && <div className="hint">{hint}</div>}
    </div>
  )
}

/** 页面里的白底说明块（Alice 在渠道模型库、模型页顶部用的内联样式）。 */
export function InfoBox({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border-medium)',
        borderRadius: 'var(--radius)',
        padding: '12px 18px',
        marginBottom: 14,
        fontSize: 12,
        color: 'var(--text-secondary)',
        lineHeight: 1.7,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

/** 带放大镜的搜索框（.search-wrap + .search-input 的宽度版）。 */
export function SearchBox({ value, onChange, placeholder, width }: { value: string; onChange: (v: string) => void; placeholder: string; width?: number }) {
  return (
    <div className="search-wrap" style={width ? { width } : undefined}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
      <input className="search-input" type="text" placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  )
}

/** 毫秒转人读：1234 → 1.2s。 */
export const fmtMs = (ms: number | null | undefined) => (ms == null ? '—' : ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`)

/** 百分比，分母为 0 显示破折。 */
export const pct = (a: number, b: number, digits = 1) => (b > 0 ? `${((a * 100) / b).toFixed(digits)}%` : '—')

/** 把上下文长度压成 8K、256K、1M。 */
export function fmtContext(n: number | null | undefined): string {
  if (!n) return '—'
  if (n >= 1_000_000) return `${+(n / 1_000_000).toFixed(1)}M`
  if (n >= 1000) return `${Math.round(n / 1000)}K`
  return String(n)
}

/** 日期输入的 YYYY-MM-DD，今天往前 n 天。 */
export function dayOffset(n: number): string {
  const d = new Date()
  d.setDate(d.getDate() - n)
  const p = (v: number) => String(v).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}
